import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { format, startOfMonth, endOfMonth, addMonths, parseISO } from 'date-fns';
import React, { useState, useMemo, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  ApiError,
  BasicFacility,
  ConnectedFacilityPublic,
  AssignmentsService,
  ConnectionsService,
  ProviderAvailabilityPublicService,
  ScheduleTypeEnum,
  ShiftRateTypesEnum,
  SourceTypeEnum,
  ProviderAvailability,
} from '../../../../../js/api';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { BaseCalendar } from '../../../components/base-calendar';
import { Button } from '../../../components/button';
import { Checkbox, CheckboxField } from '../../../components/checkbox';
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogDescription,
} from '../../../components/dialog';
import { Drawer, DrawerBody } from '../../../components/drawer';
import { Description, Field, Fieldset, Label } from '../../../components/fieldset';
import { Spinner } from '../../../components/spinner';
import { MENU_BADGES_QUERY_KEY, QUEUE_NOTIFICATIONS_QUERY_KEY } from '../../../constants/queryKeys';
import { StatusCodes } from '../../../constants/workflows';
import { useActingAsEntity } from '../../../hooks/useActingAsEntity';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { ASSIGNMENT_UPDATE_PERMISSION } from '../../../utils/permissions';
import { reactSelectReset } from '../../../utils/utils';
import { AssignmentDrawer } from './AssignmentDrawer';
import { buildBookedRanges } from './buildBookedRanges';
import {
  ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY,
  STAFF_ASSIGNMENTS_LIST_QUERY_KEY,
} from './queryKeys';
import { getCalendarFromDate, getDisabledPastDates } from './retroEntry';
import { SchedulePreview, type ShiftEntry } from './SchedulePreview';
import { ShiftConfiguration, type DateShiftConfig, type ShiftSettings } from './ShiftConfiguration';
import {
  ORG_PROVIDER_ASSIGNMENT_SHIFTS_QUERY_KEY,
  useOrgProviderAssignmentShifts,
} from './useOrgProviderAssignmentShifts';

const DATE_FORMAT = 'yyyy-MM-dd';

/** Facility choice carrying its connection's source_type — an ORGANIZATION
 * (1099) sourced connection cannot be scheduled directly. */
type ConnectedFacilityChoice = BasicFacility & { source_type?: SourceTypeEnum | null };

type FacilityOption = {
  value: string;
  label: string;
  facility: ConnectedFacilityChoice;
};

interface AssignmentCreateWrapperProps {
  /** Provider availability token — passed via URL or from the entry point */
  providerToken?: string;
  /** Provider ID — alternative to token for authenticated flows */
  providerId?: string;
  /** Pre-selected facility ID (from connection drawer entry point) */
  initialFacilityId?: string;
  /** URL to return to after creation (defaults to assignments list) */
  returnUrl?: string;
}

export const AssignmentCreateWrapper: React.FC<AssignmentCreateWrapperProps> = ({
  providerToken: propToken,
  providerId: propProviderId,
  initialFacilityId,
  returnUrl: propReturnUrl,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramToken = searchParams.get('token') || undefined;
  const paramProviderId = searchParams.get('provider') || undefined;
  const paramFacilityId = searchParams.get('facility') || undefined;
  const paramReturnUrl = searchParams.get('returnUrl') || undefined;
  const token = propToken || paramToken;
  const providerId = propProviderId || paramProviderId;
  const facilityIdFromParams = initialFacilityId || paramFacilityId;
  const returnUrl = propReturnUrl || paramReturnUrl || '/app/organization/assignments';

  const { user: _user } = useContext(SessionContext);
  const { isStaff } = useActingAsEntity();
  // Retro entry is a staff-portal capability: staff type AND the assignment
  // update code (mirrors AssignmentCreateSerializer.validate).
  const canUpdateAssignment = useHasPermission(ASSIGNMENT_UPDATE_PERMISSION);
  const canRetroEntry = isStaff && canUpdateAssignment;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useDocumentTitle('Create Assignment');

  // Core state
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [currentMonth, setCurrentMonth] = useState<Date>(() => startOfMonth(new Date()));
  // Staff-only retro entry: unlocks the last 7 days on the calendar and mutes
  // the provider request email/SMS on create. Resets each page visit.
  const [retroEntryEnabled, setRetroEntryEnabled] = useState(false);
  // Drawer state: opens when the user clicks an own-org booked day on the
  // calendar. Cleared on provider swap so a stale id doesn't survive.
  const [openAssignmentId, setOpenAssignmentId] = useState<string | null>(null);
  const [selectedFacility, setSelectedFacility] = useState<BasicFacility | null>(null);
  const [connectedFacility, setConnectedFacility] = useState<ConnectedFacilityPublic | null>(null);
  const [shiftConfigs, setShiftConfigs] = useState<Record<string, DateShiftConfig>>({});
  const [potentialSchedule, setPotentialSchedule] = useState<Record<
    string,
    DateShiftConfig
  > | null>(null);
  const [potentialScheduleShiftSettings, setPotentialScheduleShiftSettings] =
    useState<ShiftSettings | null>(null);

  // Submission state
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);
  const [assignmentSubmitError, setAssignmentSubmitError] = useState<string | null>(null);
  const [shiftErrors, setShiftErrors] = useState<Record<number, string[]>>({});
  const [createdAssignments, setCreatedAssignments] = useState<any[] | null>(null);

  // Facility modal state
  const [isFacilityModalOpen, setIsFacilityModalOpen] = useState(false);
  const [tentativeFacility, setTentativeFacility] = useState<ConnectedFacilityChoice | null>(null);
  const changeFacilityDateKeysRef = useRef<string[] | null>(null);

  // Provider modal state (used when no provider context is supplied via URL or token)
  type ProviderRef = {
    id: string;
    full_name: string;
    availability_token?: string;
    source_type?: SourceTypeEnum | null;
  };
  const [selectedProvider, setSelectedProvider] = useState<ProviderRef | null>(null);
  const [tentativeProvider, setTentativeProvider] = useState<ProviderRef | null>(null);
  const [isProviderModalOpen, setIsProviderModalOpen] = useState(false);

  // Once a provider is chosen via the modal, fold its availability_token in so
  // the existing token-driven flow (availability + facility settings) runs
  // unchanged.
  const effectiveToken = token || selectedProvider?.availability_token || undefined;

  // URL invariant: ?facility= without ?provider= is invalid because the eligible
  // facility list depends on the provider. Strip it on mount.
  useEffect(() => {
    if (!effectiveToken && !providerId && facilityIdFromParams) {
      const next = new URLSearchParams(searchParams);
      next.delete('facility');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Provider availability data
  const [allAvailableDates, setAllAvailableDates] = useState<string[]>([]);
  const [allExplicitAvailableDates, setAllExplicitAvailableDates] = useState<string[]>([]);
  const [fetchedRanges, setFetchedRanges] = useState<{ start: string; end: string }[]>([]);

  const dateRange = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(addMonths(currentMonth, 1));
    return {
      startOn: format(start, DATE_FORMAT),
      endOn: format(end, DATE_FORMAT),
    };
  }, [currentMonth]);

  const isAlreadyFetched = useMemo(() => {
    const start = parseISO(dateRange.startOn);
    const end = parseISO(dateRange.endOn);
    return fetchedRanges.some((range) => {
      const rangeStart = parseISO(range.start);
      const rangeEnd = parseISO(range.end);
      return start >= rangeStart && end <= rangeEnd;
    });
  }, [dateRange, fetchedRanges]);

  const {
    data: providerAvailabilityData,
    isInitialLoading: isLoading,
    error,
  } = useQuery({
    queryKey: ['providerAvailabilityData', effectiveToken, dateRange.startOn, dateRange.endOn],
    queryFn: async () => {
      if (!effectiveToken) throw new Error('Token is required');
      return ProviderAvailabilityPublicService.providerAvailabilityPublicRetrieve({
        availabilityToken: effectiveToken,
        startOn: dateRange.startOn,
        endOn: dateRange.endOn,
      });
    },
    enabled: !!effectiveToken && !isAlreadyFetched,
    cacheTime: 0,
    staleTime: 0,
  });

  // Sync query data into local state — runs on both fresh fetches and cache hits
  useEffect(() => {
    if (providerAvailabilityData?.available_dates) {
      const newDates = providerAvailabilityData.available_dates as unknown as string[];
      setAllAvailableDates((prev) => Array.from(new Set([...prev, ...newDates])));
    }
    if (providerAvailabilityData?.explicit_available_dates) {
      const newExplicit = providerAvailabilityData.explicit_available_dates as unknown as string[];
      setAllExplicitAvailableDates((prev) => Array.from(new Set([...prev, ...newExplicit])));
    }
    if (providerAvailabilityData) {
      setFetchedRanges((prev) => {
        const already = prev.some(
          (r) => r.start === dateRange.startOn && r.end === dateRange.endOn
        );
        return already ? prev : [...prev, { start: dateRange.startOn, end: dateRange.endOn }];
      });
    }
  }, [providerAvailabilityData, dateRange.startOn, dateRange.endOn]);

  const [lastProviderData, setLastProviderData] = useState<ProviderAvailability | null>(null);
  useEffect(() => {
    if (providerAvailabilityData) {
      setLastProviderData(providerAvailabilityData as ProviderAvailability);
    }
  }, [providerAvailabilityData]);

  const effectiveProviderData: ProviderAvailability | null =
    (providerAvailabilityData as ProviderAvailability) || lastProviderData;

  // Merge the persisted accumulator with whatever the most recent query
  // result contains. The useEffect above syncs the response into local state,
  // but there's a 1-render gap between resolution and effect run during which
  // allAvailableDates is still empty. Reading both during render closes that
  // gap so the calendar never paints "all blocked" with a fresh response in
  // hand. Same logic applied to the explicit set so the auto/explicit visual
  // distinction kicks in on the same frame the data arrives.
  const displayedAvailableDates = useMemo<string[]>(() => {
    const fromQuery =
      ((providerAvailabilityData as ProviderAvailability | undefined)
        ?.available_dates as unknown as string[]) ?? [];
    if (fromQuery.length === 0) return allAvailableDates;
    return Array.from(new Set([...allAvailableDates, ...fromQuery]));
  }, [allAvailableDates, providerAvailabilityData]);

  const displayedExplicitAvailableDates = useMemo<string[]>(() => {
    const fromQuery =
      ((providerAvailabilityData as ProviderAvailability | undefined)
        ?.explicit_available_dates as unknown as string[]) ?? [];
    if (fromQuery.length === 0) return allExplicitAvailableDates;
    return Array.from(new Set([...allExplicitAvailableDates, ...fromQuery]));
  }, [allExplicitAvailableDates, providerAvailabilityData]);

  // Buffer date keys: under STRICT, every available day that the provider
  // did NOT explicitly mark open (i.e., a ±3-day surround). Flagged in the
  // selected-dates list with a hazard icon so the rep sees they're picking
  // a day that's only open via the schedule-type buffer.
  const bufferDateKeys = useMemo<Set<string>>(() => {
    if (effectiveProviderData?.schedule_type !== ScheduleTypeEnum.STRICT) {
      return new Set<string>();
    }
    const explicit = new Set(displayedExplicitAvailableDates);
    return new Set(displayedAvailableDates.filter((iso) => !explicit.has(iso)));
  }, [
    effectiveProviderData?.schedule_type,
    displayedAvailableDates,
    displayedExplicitAvailableDates,
  ]);

  // Own-org shifts for this provider in the current calendar window — used
  // to paint booked days with status-bucket color + open the AssignmentDrawer
  // on click. Enabled only after the provider is selected.
  const { data: orgShiftsData } = useOrgProviderAssignmentShifts({
    providerId: selectedProvider?.id ?? providerId,
    start: dateRange.startOn,
    end: dateRange.endOn,
  });

  const bookedRanges = useMemo(
    () =>
      buildBookedRanges(
        orgShiftsData,
        displayedAvailableDates,
        startOfMonth(currentMonth),
        endOfMonth(addMonths(currentMonth, 1))
      ),
    [orgShiftsData, displayedAvailableDates, currentMonth]
  );

  // Resolve which provider ID to use for fetching facilities.
  // Priority: modal-selected provider > URL/prop providerId > provider derived from availability token.
  const effectiveProviderId = selectedProvider?.id || providerId || effectiveProviderData?.id;

  // Fetch eligible providers when no provider context is supplied.
  const { data: providerList } = useQuery({
    queryKey: ['assignmentEligibleProviders'],
    queryFn: () =>
      ConnectionsService.connectionsConnectedFacilityProvidersList({
        status: [StatusCodes.ACTIVE, StatusCodes.AWAIT_PRVLG],
      }),
    enabled: !providerId && !effectiveToken,
  });

  type ProviderOption = {
    value: string;
    label: string;
    provider: ProviderRef;
  };
  const providerOptions: ProviderOption[] = useMemo(() => {
    if (!providerList) return [];
    return providerList
      .filter((p) => p.id)
      .map((p) => ({
        value: p.id,
        label: p.full_name || 'Unnamed Provider',
        provider: {
          id: p.id,
          full_name: p.full_name || '',
          availability_token: p.availability_token,
          source_type: p.source_type,
        },
      }));
  }, [providerList]);

  // Open the Choose Provider modal when no provider is in scope and options are loaded.
  useEffect(() => {
    if (
      !effectiveToken &&
      !providerId &&
      !selectedProvider &&
      providerList &&
      providerList.length > 0
    ) {
      setIsProviderModalOpen(true);
    }
  }, [effectiveToken, providerId, selectedProvider, providerList]);

  // Fetch connected facilities for this provider + logged-in user's org
  const { data: connectedFacilitiesData } = useQuery({
    queryKey: ['assignmentFacilities', effectiveProviderId],
    queryFn: async () => {
      const response = await ConnectionsService.connectionsConnectedFacilityList({
        provider: effectiveProviderId,
        status: [StatusCodes.ACTIVE, StatusCodes.AWAIT_PRVLG],
        limit: 100,
      });
      return response.results.map((cf) => ({
        id: cf.facility.id,
        name: cf.facility.name,
        city: cf.facility.city,
        state: cf.facility.state,
        source_type: cf.source_type,
      }));
    },
    enabled: !!effectiveProviderId,
  });

  const facilityOptions: FacilityOption[] = useMemo(() => {
    if (!connectedFacilitiesData) return [];
    return connectedFacilitiesData
      .filter((facility) => facility.id)
      .map((facility) => ({
        value: facility.id!,
        label: facility.name || 'Unnamed Facility',
        facility: facility as ConnectedFacilityChoice,
      }));
  }, [connectedFacilitiesData]);

  // Auto-select facility if initialFacilityId is provided — except when that
  // connection is ORGANIZATION (1099) sourced: route through the chooser so
  // the cannot-schedule warning shows instead of silently proceeding.
  useEffect(() => {
    if (facilityIdFromParams && connectedFacilitiesData && !selectedFacility) {
      const match = connectedFacilitiesData.find((f) => f.id === facilityIdFromParams);
      if (!match) return;
      if (match.source_type === SourceTypeEnum.ORGANIZATION) {
        setTentativeFacility(match as ConnectedFacilityChoice);
        setIsFacilityModalOpen(true);
        return;
      }
      setSelectedFacility(match as BasicFacility);
    }
  }, [facilityIdFromParams, connectedFacilitiesData, selectedFacility]);

  // Reset availability state whenever the modal-picked provider changes so the
  // calendar doesn't bleed the previous provider's highlighted dates into the
  // new one before the fresh availability fetch resolves.
  useEffect(() => {
    setAllAvailableDates([]);
    setAllExplicitAvailableDates([]);
    setFetchedRanges([]);
    setOpenAssignmentId(null);
  }, [selectedProvider?.id]);

  // If the active provider changes (typically via the Choose Provider modal)
  // and the previously-chosen facility isn't in the new provider's list, drop
  // the facility (plus dependent staged state) and re-prompt.
  useEffect(() => {
    if (
      selectedFacility &&
      connectedFacilitiesData &&
      !connectedFacilitiesData.some((f) => f.id === selectedFacility.id)
    ) {
      setSelectedFacility(null);
      setShiftConfigs({});
      setPotentialSchedule(null);
      setPotentialScheduleShiftSettings(null);
      setIsFacilityModalOpen(true);
    }
  }, [connectedFacilitiesData, selectedFacility]);

  // Open facility modal on first load if no facility selected
  useEffect(() => {
    if (
      connectedFacilitiesData &&
      connectedFacilitiesData.length > 0 &&
      !selectedFacility &&
      !facilityIdFromParams
    ) {
      setIsFacilityModalOpen(true);
    }
  }, [connectedFacilitiesData, selectedFacility, facilityIdFromParams]);

  // Fetch connected facility details (shift settings) when facility is selected
  useEffect(() => {
    if (!selectedFacility?.id || !effectiveProviderData?.availability_token) {
      setConnectedFacility(null);
      return;
    }

    const defaultSettings = {
      shift_rate_types: [
        ShiftRateTypesEnum.DAY,
        ShiftRateTypesEnum.NIGHT,
        ShiftRateTypesEnum._24H,
        ShiftRateTypesEnum.CALL,
      ],
      shift_length: 12,
      shift_start_day: '07:00:00',
      shift_start_night: '19:00:00',
      shift_start_24h: '07:00:00',
    } as ConnectedFacilityPublic;

    ConnectionsService.connectionsConnectedFacilityPublicLookupRetrieve({
      facility: selectedFacility.id,
      availabilityToken: effectiveProviderData.availability_token,
    })
      .then((data) => setConnectedFacility(data))
      .catch(() => setConnectedFacility(defaultSettings));
  }, [selectedFacility?.id, effectiveProviderData?.availability_token]);

  // Disabled dates — past days of the current month, unless retro entry has
  // unlocked the trailing 7-day window (then fromDate owns the boundary).
  const disabledDates = useMemo(() => getDisabledPastDates(retroEntryEnabled), [retroEntryEnabled]);

  // Earliest navigable/selectable date; undefined = BaseCalendar's default
  // (start of the current month). Retro reaches back 7 days, crossing into
  // the previous month when the window spans it.
  const calendarFromDate = useMemo(
    () => getCalendarFromDate(retroEntryEnabled),
    [retroEntryEnabled]
  );

  const handleRetroToggle = (checked: boolean) => {
    setRetroEntryEnabled(checked);
    if (!checked) {
      // Prune already-selected past dates so a disabled toggle can't submit them.
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setSelectedDates((prev) => prev.filter((d) => d.getTime() >= today.getTime()));
    }
  };

  const makeFacilityRef = (f: BasicFacility) => ({
    id: f.id || '',
    name: f.name || '',
  });

  // Handle facility confirmation from modal
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleFacilityConfirm = useCallback(() => {
    if (!tentativeFacility) return;
    const facilityRef = makeFacilityRef(tentativeFacility);

    const dateKeys = changeFacilityDateKeysRef.current;
    if (dateKeys && dateKeys.length > 0) {
      // Per-shift override: apply only to selected date rows, creating configs if needed
      setShiftConfigs((prev) => {
        const updated = { ...prev };
        for (const key of dateKeys) {
          const existing = updated[key] || { shiftType: null, onCall: false, facility: null };
          updated[key] = { ...existing, facility: facilityRef };
        }
        return updated;
      });
    } else {
      // Assignment-level facility change: update default and all selected dates
      setSelectedFacility(tentativeFacility);
      setShiftConfigs((prev) => {
        const updated = { ...prev };
        // Update existing configs
        for (const [key, config] of Object.entries(updated)) {
          updated[key] = { ...config, facility: facilityRef };
        }
        // Create configs for selected dates that don't have one yet
        for (const date of selectedDates) {
          const key = format(date, DATE_FORMAT);
          if (!updated[key]) {
            updated[key] = { shiftType: null, onCall: false, facility: facilityRef };
          }
        }
        return updated;
      });
    }

    changeFacilityDateKeysRef.current = null;
    setIsFacilityModalOpen(false);
    setTentativeFacility(null);
  }, [tentativeFacility, selectedDates]);

  // When new dates are selected, ensure they have a facility config
  useEffect(() => {
    if (!selectedFacility) return;
    const facilityRef = makeFacilityRef(selectedFacility);
    setShiftConfigs((prev) => {
      const updated = { ...prev };
      let changed = false;
      for (const date of selectedDates) {
        const key = format(date, DATE_FORMAT);
        if (!updated[key]) {
          updated[key] = { shiftType: null, onCall: false, facility: facilityRef };
          changed = true;
        } else if (!updated[key].facility) {
          updated[key] = { ...updated[key], facility: facilityRef };
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
  }, [selectedDates, selectedFacility]);

  // Validate and submit
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleAssignmentSubmit = async (shiftEntries: ShiftEntry[], comment: string) => {
    if (shiftEntries.length === 0) {
      setAssignmentSubmitError('Please add at least one shift');
      return;
    }
    if (!selectedFacility) {
      setAssignmentSubmitError('Facility is required');
      return;
    }

    setIsSubmittingAssignment(true);
    setAssignmentSubmitError(null);
    setShiftErrors({});

    const TIME_FORMAT = 'HH:mm:ss';
    try {
      const shifts = shiftEntries.map((entry) => ({
        facility: entry.facility?.id || selectedFacility.id || '',
        shift_type: entry.shiftType,
        shift_rate_type: (entry.onCall
          ? ShiftRateTypesEnum.CALL
          : entry.shiftType) as ShiftRateTypesEnum,
        start_on: format(entry.startTime, DATE_FORMAT),
        start_time: format(entry.startTime, TIME_FORMAT),
        end_on: format(entry.endTime, DATE_FORMAT),
        end_time: format(entry.endTime, TIME_FORMAT),
        bonus_amount: entry.bonus_amount || 0,
        holiday_amount: entry.holiday_amount || 0,
      }));

      const response = await AssignmentsService.assignmentsAssignmentCreate({
        requestBody: {
          provider: effectiveProviderId || '',
          facility: selectedFacility.id || '',
          shifts,
          comment: comment.trim() || '',
          is_retro_entry: retroEntryEnabled,
        } as any,
      });

      const responseData = response as any;
      const assignments = responseData?.assignments || [responseData];
      const count = responseData?.count || assignments.length;

      toast.success(
        count === 1
          ? 'Assignment created successfully!'
          : `${count} assignments created successfully!`
      );
      queryClient.invalidateQueries({ queryKey: [ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [STAFF_ASSIGNMENTS_LIST_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MENU_BADGES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY] });
      // Refresh the calendar's own data sources so the just-created shifts
      // immediately paint as pending instead of as "blocked" (which is what
      // the user would see otherwise: backend availability drops the day,
      // but the shift list is still stale, so the day falls into the blocked
      // overlay until the cache catches up).
      queryClient.invalidateQueries({ queryKey: [ORG_PROVIDER_ASSIGNMENT_SHIFTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['providerAvailabilityData'] });
      setAllAvailableDates([]);
      setAllExplicitAvailableDates([]);
      setFetchedRanges([]);
      setCreatedAssignments(assignments);
    } catch (err) {
      if (err instanceof ApiError && err.body?.shifts && Array.isArray(err.body.shifts)) {
        const parsedShiftErrors: Record<number, string[]> = {};
        err.body.shifts.forEach((shiftErrorObj: Record<string, string[]>, index: number) => {
          if (
            shiftErrorObj &&
            typeof shiftErrorObj === 'object' &&
            Object.keys(shiftErrorObj).length > 0
          ) {
            const errorMessages: string[] = [];
            Object.values(shiftErrorObj).forEach((dateErrors) => {
              if (Array.isArray(dateErrors)) errorMessages.push(...dateErrors);
            });
            if (errorMessages.length > 0) parsedShiftErrors[index] = errorMessages;
          }
        });
        setShiftErrors(parsedShiftErrors);
      } else {
        const fallback = 'Failed to create assignment. Please try again.';
        let errorMessage = fallback;
        if (err instanceof ApiError) {
          const body = err.body;
          if (Array.isArray(body)) {
            errorMessage = body.join('. ');
          } else if (body && typeof body === 'object') {
            if (body.detail) {
              errorMessage = body.detail;
            } else if (body.message) {
              errorMessage = body.message;
            } else if (body.non_field_errors) {
              errorMessage = Array.isArray(body.non_field_errors)
                ? body.non_field_errors.join('. ')
                : body.non_field_errors;
            }
          } else if (typeof body === 'string') {
            errorMessage = 'An error occurred. Please contact support.';
          }
          // If we still have the fallback but err.message has something useful
          if (errorMessage === fallback && err.message && err.message !== 'Internal Server Error') {
            errorMessage = err.message;
          }
        } else if (err instanceof Error && err.message) {
          errorMessage = err.message;
        }
        setAssignmentSubmitError(errorMessage);
      }
    } finally {
      setIsSubmittingAssignment(false);
    }
  };

  // --- Render guards ---

  // When the wrapper is reached with no provider context at all, the user
  // selects one via the Choose Provider modal — so we fall through to render
  // and let the modal drive provider selection.
  const canPromptForProvider = !providerId && !token;

  if (!effectiveToken && !providerId && !canPromptForProvider) {
    return (
      <div className="tw:p-4 tw:max-w-6xl tw:mx-auto tw:w-full">
        <div className="tw:bg-white tw:rounded-xl tw:p-8 tw:border tw:border-zinc-200 tw:text-center">
          <p className="tw:text-stoplight-danger">Missing provider information</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw:p-4 tw:max-w-6xl tw:mx-auto tw:w-full">
        <div className="tw:bg-white tw:rounded-xl tw:p-8 tw:border tw:border-zinc-200 tw:text-center">
          <p className="tw:text-stoplight-danger">Error loading provider availability</p>
        </div>
      </div>
    );
  }

  if (!effectiveProviderData && !isLoading && !canPromptForProvider) {
    return (
      <div className="tw:p-4 tw:max-w-6xl tw:mx-auto tw:w-full">
        <div className="tw:bg-white tw:rounded-xl tw:p-8 tw:border tw:border-zinc-200 tw:text-center">
          <p className="tw:text-zinc-500">No provider data available</p>
        </div>
      </div>
    );
  }

  // Determine the selected facility option for the dropdown
  const selectedFacilityOption = tentativeFacility
    ? facilityOptions.find((o) => o.value === tentativeFacility.id) || null
    : null;

  return (
    <div className="tw:p-4 tw:max-w-6xl tw:mx-auto tw:w-full">
      <div className="tw:flex tw:flex-col-reverse tw:lg:flex-row tw:gap-4 tw:lg:gap-2">
        {/* Calendar and Scheduler */}
        <div className="tw:flex tw:flex-col tw:gap-2">
          <div
            className={clsx(
              'tw:md:bg-white tw:rounded-xl tw:md:p-4 tw:md:border tw:border-zinc-200 tw:relative',
              potentialSchedule && potentialScheduleShiftSettings && 'tw:hidden'
            )}
          >
            {isLoading && (
              <div className="tw:absolute tw:inset-0 tw:bg-white/50 tw:z-10 tw:flex tw:items-center tw:justify-center tw:rounded-xl">
                <Spinner />
              </div>
            )}
            <div className="tw:flex tw:flex-col">
              <div className="tw:flex tw:flex-row tw:items-start tw:justify-between tw:gap-4">
                <div className="tw:flex tw:flex-col tw:items-start tw:justify-center">
                  <h2 className="tw:!text-base tw:!font-semibold tw:!text-brand-green-0 tw:!mb-1">
                    Select Available Days
                  </h2>
                  <p className="tw:hidden tw:md:block tw:!text-xs tw:font-semibold tw:!text-zinc-500">
                    Hold Shift + click to select ranges of days
                  </p>
                </div>
                {canRetroEntry && (
                  <CheckboxField className="tw:shrink-0">
                    <Checkbox
                      color="brandgreen1"
                      checked={retroEntryEnabled}
                      onChange={handleRetroToggle}
                    />
                    <Label>Enable Retro Entry</Label>
                    <Description className="tw:!text-xs">
                      Allows selecting shifts up to 7 days in the past.
                    </Description>
                  </CheckboxField>
                )}
              </div>
              <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-x-4 tw:gap-y-2 tw:mt-1 tw:mb-4">
                <div className="tw:flex tw:items-center tw:gap-2">
                  <div className="tw:w-4 tw:h-4 tw:rounded tw:bg-brand-green-2 tw:border-solid tw:border tw:border-brand-green-1/50" />
                  <span className="tw:text-xs tw:text-zinc-600">Available</span>
                </div>
                <div className="tw:flex tw:items-center tw:gap-2">
                  <div className="tw:w-4 tw:h-4 tw:rounded tw:bg-brand-green-0" />
                  <span className="tw:text-xs tw:text-zinc-600">Selected</span>
                </div>
                <div className="tw:flex tw:items-center tw:gap-2">
                  <div className="tw:w-4 tw:h-4 tw:rounded tw:bg-amber-100 tw:border tw:border-amber-300" />
                  <span className="tw:text-xs tw:text-zinc-600">Pending</span>
                </div>
                <div className="tw:flex tw:items-center tw:gap-2">
                  <div className="tw:w-4 tw:h-4 tw:rounded tw:bg-emerald-100 tw:border tw:border-emerald-300" />
                  <span className="tw:text-xs tw:text-zinc-600">Confirmed</span>
                </div>
                <div className="tw:flex tw:items-center tw:gap-2">
                  <div className="tw:w-4 tw:h-4 tw:rounded tw:bg-zinc-200 tw:border tw:border-zinc-400" />
                  <span className="tw:text-xs tw:text-zinc-600">Completed</span>
                </div>
                <div className="tw:flex tw:items-center tw:gap-2">
                  <div className="calendar-blocked-pattern tw:w-4 tw:h-4 tw:rounded tw:border tw:border-zinc-300" />
                  <span className="tw:text-xs tw:text-zinc-600">Blocked</span>
                </div>
              </div>
            </div>
            <BaseCalendar
              availableDates={displayedAvailableDates}
              // STRICT is the only mode that needs the explicit/auto split:
              // explicit allows render solid green, auto ±3 days render as
              // plain default cells (per the `.rdp-day_available_auto` CSS
              // override). OPEN already returns empty explicit_available_dates,
              // and CLOSED treats every available day as explicit — so for
              // both, omitting the prop is the right call (no split at all).
              explicitAvailableDates={
                effectiveProviderData?.schedule_type === ScheduleTypeEnum.STRICT
                  ? displayedExplicitAvailableDates
                  : undefined
              }
              selectedDates={selectedDates}
              onSelectedDatesChange={setSelectedDates}
              numberOfMonths={2}
              disabled={!!potentialSchedule}
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
              fromDate={calendarFromDate}
              disabledDates={disabledDates}
              bookedRanges={bookedRanges}
              onBookedDateClick={(assignmentId) => setOpenAssignmentId(assignmentId)}
              className="availability-calendar justify-self-center"
            />
            {!selectedFacility && (
              <div className="tw:flex tw:justify-end tw:my-4">
                <Button color="brand-green-outline" onClick={() => setIsFacilityModalOpen(true)}>
                  Choose Facility
                </Button>
              </div>
            )}
          </div>

          {/* Shift Configuration or Schedule Preview */}
          {selectedFacility &&
            (potentialSchedule && potentialScheduleShiftSettings ? (
              <SchedulePreview
                selectedDates={selectedDates}
                shiftConfigs={potentialSchedule}
                shiftSettings={potentialScheduleShiftSettings}
                onBack={() => {
                  setPotentialSchedule(null);
                  setPotentialScheduleShiftSettings(null);
                  setShiftErrors({});
                }}
                onRemoveDate={(dateKey) => {
                  setSelectedDates((prev) =>
                    prev.filter((date) => format(date, DATE_FORMAT) !== dateKey)
                  );
                  setPotentialSchedule((prev) => {
                    if (!prev) return null;
                    const updated = { ...prev };
                    delete updated[dateKey];
                    return Object.keys(updated).length > 0 ? updated : null;
                  });
                }}
                onSubmit={handleAssignmentSubmit}
                isSubmitting={isSubmittingAssignment}
                submitError={assignmentSubmitError}
                shiftErrors={shiftErrors}
                bufferDateKeys={bufferDateKeys}
              />
            ) : (
              <ShiftConfiguration
                connectedFacility={connectedFacility}
                selectedDates={selectedDates}
                shiftConfigs={shiftConfigs}
                onShiftConfigChange={setShiftConfigs}
                onPreviewSchedule={(config, shiftSettings) => {
                  setPotentialSchedule(config);
                  setPotentialScheduleShiftSettings(shiftSettings);
                }}
                onRemoveAllDates={() => {
                  setSelectedDates([]);
                  setShiftConfigs({});
                }}
                onChangeFacility={(dateKeys) => {
                  changeFacilityDateKeysRef.current = dateKeys;
                  setTentativeFacility(null);
                  setIsFacilityModalOpen(true);
                }}
                bufferDateKeys={bufferDateKeys}
              />
            ))}
        </div>

        {/* Provider and Facility info sidebar */}
        <aside className="tw:md:bg-white tw:md:rounded-xl tw:md:py-4 tw:lg:p-6 tw:border-b tw:pb-3 tw:md:border tw:border-zinc-200 tw:flex tw:flex-row tw:lg:flex-col tw:gap-1 tw:md:gap-4 tw:lg:min-w-64 tw:h-fit">
          <div className="tw:flex tw:flex-col tw:items-center tw:gap-1 tw:md:gap-3 tw:flex-1">
            <div className="tw:h-12 tw:w-12 tw:md:h-16 tw:md:w-16 tw:rounded-full tw:bg-brand-green-0/10 tw:flex tw:items-center tw:justify-center tw:text-brand-green-0 tw:text-lg tw:md:text-2xl">
              <i className="fas fa-xl fa-user-doctor" />
            </div>
            <div className="tw:text-center tw:min-h-[3rem] tw:flex tw:flex-col tw:justify-center">
              {effectiveProviderData ? (
                <>
                  <p className="tw:text-xs tw:md:text-sm tw:font-semibold tw:text-zinc-900 tw:!mb-1">
                    {effectiveProviderData.display_name}
                  </p>
                  {effectiveProviderData.specialties &&
                    effectiveProviderData.specialties.length > 0 && (
                      <p className="tw:text-[8pt] tw:md:text-xs tw:text-zinc-500 tw:!mb-0">
                        {effectiveProviderData.specialties[0]}
                      </p>
                    )}
                </>
              ) : selectedProvider ? (
                <p className="tw:text-xs tw:md:text-sm tw:font-semibold tw:text-zinc-900 tw:!mb-1">
                  {selectedProvider.full_name}
                </p>
              ) : canPromptForProvider ? (
                <Button
                  color="brand-green-outline"
                  onClick={() => {
                    setTentativeProvider(null);
                    setIsProviderModalOpen(true);
                  }}
                >
                  Choose Provider
                </Button>
              ) : (
                <div className="tw:h-4 tw:w-24 tw:bg-zinc-200 tw:animate-pulse tw:rounded tw:mx-auto" />
              )}
              {canPromptForProvider && selectedProvider && (
                <button
                  type="button"
                  className="tw:!text-xs tw:text-brand-green-1 tw:underline tw:cursor-pointer tw:!mt-1 tw:!mb-0 tw:bg-transparent tw:border-none tw:p-0"
                  onClick={() => {
                    setTentativeProvider(selectedProvider);
                    setIsProviderModalOpen(true);
                  }}
                >
                  Change
                </button>
              )}
            </div>

            {/* Phone Number */}
            {effectiveProviderData?.phone_number && (
              <div className="tw:flex tw:items-center tw:justify-center tw:gap-2 tw:text-xs tw:md:text-sm">
                <i className="tw:!hidden tw:md:!block fa-solid fa-phone tw:text-zinc-400" />
                <a
                  href={`tel:${effectiveProviderData.phone_number}`}
                  className="tw:text-zinc-700 hover:tw:text-zinc-900 tw:!no-underline"
                >
                  {effectiveProviderData.phone_number}
                </a>
              </div>
            )}
          </div>

          {/* Facility card */}
          {selectedFacility && (
            <div className="tw:flex-1 tw:border-l tw:border-zinc-200 tw:pl-4 tw:lg:border-l-0 tw:lg:pl-0 tw:lg:border-t tw:lg:pt-4">
              <div className="tw:flex tw:flex-col tw:items-center tw:text-center">
                <div className="tw:h-12 tw:w-12 tw:md:h-16 tw:md:w-16 tw:rounded-full tw:bg-brand-green-0/10 tw:flex tw:items-center tw:justify-center tw:text-brand-green-0 tw:text-lg tw:md:text-2xl tw:!mb-3">
                  <i className="fas fa-xl fa-hospital" />
                </div>
                <p className="tw:text-xs tw:md:text-sm tw:font-semibold tw:text-zinc-900 tw:!mb-1">
                  {selectedFacility.name}
                </p>
                {selectedFacility.city && selectedFacility.state && (
                  <p className="tw:text-[8pt] tw:md:text-xs tw:text-zinc-500 tw:!mb-0">
                    {selectedFacility.city}, {selectedFacility.state}
                  </p>
                )}
                {!(potentialSchedule && potentialScheduleShiftSettings) && (
                  <button
                    type="button"
                    className="tw:!text-xs tw:text-brand-green-1 tw:underline tw:cursor-pointer tw:!mt-1 tw:!mb-0 tw:bg-transparent tw:border-none tw:p-0"
                    onClick={() => {
                      setTentativeFacility(selectedFacility);
                      setIsFacilityModalOpen(true);
                    }}
                  >
                    Change
                  </button>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* Assignment Created Dialog */}
      <Dialog
        open={!!createdAssignments}
        onClose={() => {
          setCreatedAssignments(null);
          navigate(returnUrl);
        }}
      >
        <DialogTitle className="tw:!text-base tw:!font-semibold tw:!mb-1 tw:p-2 tw:rounded-lg tw:bg-brand-green-0 tw:!text-white">
          {createdAssignments?.length === 1 ? 'Assignment Created' : 'Assignments Created'}
        </DialogTitle>
        <DialogBody>
          {createdAssignments && createdAssignments.length > 1 && (
            <p className="tw:text-sm tw:text-zinc-600 tw:!mb-3">
              Your shifts were split into {createdAssignments.length} assignments based on
              scheduling gaps.
            </p>
          )}
          <div className="tw:space-y-2">
            {createdAssignments?.map((assignment: any) => (
              <button
                key={assignment.id}
                type="button"
                className="tw:w-full tw:text-left tw:p-3 tw:rounded-lg tw:border tw:border-zinc-200 tw:hover:border-brand-green-1 tw:hover:bg-brand-green-3 tw:cursor-pointer tw:transition-colors tw:bg-white"
                onClick={() => {
                  setCreatedAssignments(null);
                  navigate('/app/organization/assignments/' + assignment.id);
                }}
              >
                <div className="tw:flex tw:justify-between tw:items-center">
                  <div>
                    <p className="tw:text-sm tw:font-medium tw:text-zinc-900 tw:!mb-0">
                      {assignment.start_on} — {assignment.end_on}
                    </p>
                    <p className="tw:text-xs tw:text-zinc-500 tw:!mb-0">
                      {assignment.facility?.name && (
                        <span className="tw:mr-2">{assignment.facility.name}</span>
                      )}
                      {assignment.shifts?.length || 0} shift
                      {(assignment.shifts?.length || 0) !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <i className="far fa-chevron-right tw:text-zinc-400" />
                </div>
              </button>
            ))}
          </div>
        </DialogBody>
        <DialogActions>
          <Button
            plain
            onClick={() => {
              setCreatedAssignments(null);
              navigate(returnUrl);
            }}
          >
            Return
          </Button>
        </DialogActions>
      </Dialog>

      {/* Facility Selection Modal */}
      <Dialog
        open={isFacilityModalOpen}
        onClose={() => {
          setIsFacilityModalOpen(false);
          setTentativeFacility(null);
        }}
        className="tw:!p-3"
      >
        <DialogTitle className="tw:!text-base tw:!font-semibold tw:!mb-1 tw:p-2 tw:rounded-lg tw:bg-brand-green-0 tw:!text-white">
          Choose Facility
        </DialogTitle>
        <DialogBody className="tw:!mt-0">
          <Fieldset>
            <DialogDescription>
              Where do you wish to schedule {effectiveProviderData?.display_name}?
            </DialogDescription>
            <Field>
              <Select
                id="facility"
                options={facilityOptions}
                value={selectedFacilityOption}
                onChange={(option) => setTentativeFacility(option?.facility || null)}
                placeholder="Start typing to search"
                className="tw:w-full"
                styles={reactSelectReset}
                isClearable={false}
              />
            </Field>
            {tentativeFacility?.source_type === SourceTypeEnum.ORGANIZATION && (
              <div className="tw:mt-4 tw:rounded-md tw:bg-stoplight-warning/10 tw:p-4">
                <div className="tw:flex">
                  <div className="tw:flex-shrink-0">
                    <i className="fa-solid fa-triangle-exclamation tw:text-stoplight-warning" />
                  </div>
                  <div className="tw:ml-3">
                    <p className="tw:text-sm tw:text-stoplight-warning tw:!mb-0">
                      1099-sourced connections cannot be scheduled directly. Assignments are created
                      from external integration.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button
            plain
            onClick={() => {
              setIsFacilityModalOpen(false);
              setTentativeFacility(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="brandgreen0"
            disabled={
              !tentativeFacility || tentativeFacility.source_type === SourceTypeEnum.ORGANIZATION
            }
            onClick={handleFacilityConfirm}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Provider Selection Modal — fires only when the wrapper is reached with
          no provider context (no token, no providerId). */}
      <Dialog
        open={isProviderModalOpen}
        onClose={() => {
          setIsProviderModalOpen(false);
          setTentativeProvider(null);
        }}
        className="tw:!p-3"
      >
        <DialogTitle className="tw:!text-base tw:!font-semibold tw:!mb-1 tw:p-2 tw:rounded-lg tw:bg-brand-green-0 tw:!text-white">
          Choose Provider
        </DialogTitle>
        <DialogBody className="tw:!mt-0">
          <Fieldset>
            <DialogDescription>Which provider do you wish to schedule?</DialogDescription>
            <Field>
              <Select
                id="provider"
                options={providerOptions}
                value={
                  tentativeProvider
                    ? providerOptions.find((o) => o.value === tentativeProvider.id) || null
                    : null
                }
                onChange={(option) => setTentativeProvider(option?.provider || null)}
                placeholder="Start typing to search"
                className="tw:w-full"
                styles={reactSelectReset}
                isClearable={false}
              />
            </Field>
            {tentativeProvider?.source_type === SourceTypeEnum.ORGANIZATION && (
              <div className="tw:mt-4 tw:rounded-md tw:bg-stoplight-warning/10 tw:p-4">
                <div className="tw:flex">
                  <div className="tw:flex-shrink-0">
                    <i className="fa-solid fa-triangle-exclamation tw:text-stoplight-warning" />
                  </div>
                  <div className="tw:ml-3">
                    <p className="tw:text-sm tw:text-stoplight-warning tw:!mb-0">
                      1099 Providers cannot be scheduled directly. Assignments are created from
                      external integration.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button
            plain
            onClick={() => {
              setIsProviderModalOpen(false);
              setTentativeProvider(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="brandgreen0"
            disabled={
              !tentativeProvider || tentativeProvider.source_type === SourceTypeEnum.ORGANIZATION
            }
            onClick={() => {
              setSelectedProvider(tentativeProvider);
              setTentativeProvider(null);
              setIsProviderModalOpen(false);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      {openAssignmentId && (
        <Drawer open onClose={() => setOpenAssignmentId(null)} width="3xl" mode="modal">
          <DrawerBody className="tw:!text-sm/6">
            <AssignmentDrawer
              assignmentId={openAssignmentId}
              onClose={() => setOpenAssignmentId(null)}
            />
          </DrawerBody>
        </Drawer>
      )}
    </div>
  );
};
